import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { isOrderClosed } from '../../utils/order';
import { zCancelledReason, zOrderStatus, zPlatform, zServiceType } from '../enums';
import { zPrice } from '../primitives';
import { orderItemSchema, orderItemStrictSchema } from './orderItemSchema';
import { paymentSchema, paymentStrictSchema } from './paymentSchema';

// 1. Base Schema - Form initialization (shape + defaults only)
// Only main primitives: z.string(), z.number(), z.array(), etc.
// No strict validations like .min(), .int(), .guid()
export const orderSchema = z.object({
  /** TODO: Rename to `cancelReason` to avoid confusion.
   *    American English: canceledReason (one L)
   *    British English: cancelledReason (two Ls)
   * */
  cancelledReason: zCancelledReason.nullable().default(null),
  confirmationToken: z.string().nullable().default(null),
  confirmedAt: z.number().nullable().default(null),
  customerEmail: z.string().nullable().default(null), // TODO: Consider adding .email validation? Add to `orderPlatformSchema`?
  /** `customerName` represents the name for the order. Defaults to `restaurantCustomer.name`, but can be overridden during order checkout. **/
  customerName: z.string().nullable().default(null),
  customerPhoneNumber: z.string().nullable().default(null), // TODO: Add validation to ensure international format. Ie +61.
  deliveryAddress: z.string().nullable().default(null),
  deliveryNotes: z.string().nullable().default(null),
  deliverySuburbFee: z.number().nullable().default(null),
  deliverySuburbName: z.string().nullable().default(null),
  deliverySuburbPostcode: z.string().nullable().default(null),
  deliverySuburbUuid: z.string().nullable().default(null),
  discountCode: z.string().nullable().default(null).optional(),
  discountPercent: z.number().nullable().default(null),
  /**
   * `order.discountTotal` only represents order level discounts.
   * `calcAllDiscounts()` can be used to also include order item discounts.
   **/
  discountTotal: z.number().default(0),
  registerClosureId: z.string().nullable().default(null),
  estWaitTime: z.number().nullable().default(null),
  /**
   * The time at which the standalone printer fetched the order.
   * Or, the time the order was successfully fetched by the POS terminal (and new order alert shown).
   * (only applies to online orders)
   */
  fetchedAt: z.number().nullable().default(null),
  fullyPaidAt: z.number().nullable().default(null),
  legacyRestaurantId: z.number().nullable().default(null),
  /**
   * `localCreatedAt` is a timestamp indicating when the POS system first saved this order to local storage.
   *
   * This field is NOT set:
   * - When creating a new order in memory
   * - By the client for non-POS orders (though clients can use it for their own logic,
   *   e.g., consumer-web uses it to control order expiration in local storage)
   *
   * For non-POS orders, the server sets or overrides this field to enable consistent
   * report filtering across all order types.
   */
  localCreatedAt: z.number().nullable().default(null),
  /**
   * - Only the primary POS is responsible for generating the local order id, using `nextLocalOrderId` from store.
   * - Secondary POS terminal can generate an order, but with `localOrderId` set to 0.
   * - The primary POS will then generate a `localOrderId`, once they are synced on primary POS.
   * - The `nextLocalOrderId` persists to local storage so that it can be maintained across app restarts.
   * - For standalone printers, `standalonePrinterNextLocalOrderId` from PlatformGlobals is used.
   */
  localOrderId: z.number().nullable().default(null),
  /**
   * `localUpdatedAt` is a timestamp representing when the POS last committed this order to its local storage.
   *
   * This field is ONLY updated when the order is saved to local storage via `saveOrder()`.
   *
   * This field is NOT updated:
   * - During in-memory cart modifications (adding items, changing quantities, etc.)
   * - When initialising a new in-memory order
   * - For non-POS orders. It always remains `null` if `order.platform` !== 'POS'
   *
   * Use cases:
   * - Determining sync conflicts between POS and server versions
   * - Identifying which orders have been committed to storage
   * - Tracking when orders were last persisted locally
   *
   * Note: Use `order.hasUnsavedChanges` to detect uncommitted cart changes.
   */
  localUpdatedAt: z.number().nullable().default(null),
  /**
   * Indicates whether the order was last saved by a secondary POS terminal.
   * - `true`: Last saved by secondary POS
   * - `false`: Last saved by primary POS
   * - `null`: Not a POS order (e.g., online orders)
   *
   * Used primarily to filter orders that need primary POS processing.
   * TODO: Need to work out how orders marked as closed (e.g. voided) can be synced to primary POS.
   *       This will also be an issue later for non-primary terminals that have register attached and can close orders.
   *       Potential solution: Like we have `syncedOpenOrders`, syncedClosedOrders orders queues,
   *       we could have `secondaryPosOrders` that filters on `lastSavedBySecondaryPos`.
   */
  lastSavedBySecondaryPos: z.boolean().nullable().default(null).optional(), // Remove .optional() after a Convex migration
  /** Formatted name of the passcode user who created this order (e.g., "ADAM W"). */
  createdBy: z.string().nullable().default(null).optional(),
  loyaltyCardFreeCoffeesRedeemed: z.number().nullable().default(null),
  loyaltyCardStampsEarned: z.number().nullable().default(null),
  notes: z.string().nullable().default(null),
  orderItems: z.array(orderItemSchema).default([]),
  orderItemsTotal: z.number().default(0),
  payments: z.array(paymentSchema).default([]),
  platform: zPlatform,
  /** `restaurantCustomerId` should be used in place of `customerAwsId` **/
  restaurantCustomerId: z.string().nullable().default(null),
  restaurantId: z.string(),
  scheduledJobIds: z.array(z.string()).default([]),
  serviceType: zServiceType.default('PICKUP'), // Ie takeaway order. TODO: Change default to 'NONE'?
  status: zOrderStatus,
  /**
   * When an order enters a "closed" state, it's a terminal/final state - no further status changes can be made.
   * An order is considered "closed" if its `status` is "CANCELLED" or "COMPLETED".
   * Order defaults to an "open" order, ie `isClosed` set to `false`.
   **/
  isClosed: z.boolean().default(false),
  submittedAt: z.number().nullable().default(null),
  surchargePercent: z.number().nullable().default(null),
  /**
   * `order.surchargeTotal` only represents order level surcharges.
   * `calcAllSurcharges()` can be used to also include order item surcharges.
   **/
  surchargeTotal: z.number().default(0),
  table: z.string().nullable().default(null),
  total: z.number().default(0),
  uuid: z.string().default(() => uuidv4()),
  voidedAt: z.number().nullable().default(null),
  /**
   * `version` will be `null` for client initiated orders. Once synced `version` will starts at 1.
   * This approach avoids comparing server timestamps with client timestamps (which avoids any epoch clock skew issues).
   * It also allow server originated order changes (eg payment.status) to be correctly synced to the POS.
   */
  version: z.number().nullable().default(null).optional(), // Remove .optional() after a Convex migration

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  /**
   * Timestamp of the last server-side save when the order's `version` number was incremented.
   * Applies to all order platform types.
   */
  updatedAt: z.number().optional(),
});
export type Order = z.output<typeof orderSchema>;

// 2. Form Submission Schema - Strict validations, no defaults
// .int(), .guid(), .email(), custom primitives (zText, zPrice) go here
// Never use .uuid(), use .guid() instead
export const orderStrictSchema = orderSchema
  .safeExtend({
    deliverySuburbFee: zPrice.nullable(),
    deliverySuburbUuid: z.guid().nullable(),
    discountTotal: zPrice,
    orderItems: z.array(orderItemStrictSchema),
    orderItemsTotal: zPrice,
    payments: z.array(paymentStrictSchema),
    surchargeTotal: zPrice,
    table: z.string().max(3).nullable(),
    total: zPrice,
    uuid: z.guid(),
    customerPhoneNumber: z
      .string()
      .nullable()
      /** customerPhoneNumber - Validate: Checks it's valid local number, eg 0466302332 or 0352438055 */
      .refine(
        (val) => {
          if (!val) return true; // If it's null or '' then continue on to transform step

          // Local format
          if (val.startsWith('0')) {
            return /^0\d{9}$/.test(val); // 0 followed by 9 digits
          }

          // Int format
          const COUNTRY_CODE = '+61';
          if (val.startsWith('+')) {
            return new RegExp(`^\\${COUNTRY_CODE}\\d{9}$`).test(val); // +61 followed by 9 digits
          }
        },
        {
          message: 'Use valid format. Eg 04xxxxxxxx',
        },
      )
      /** customerPhoneNumber - after parsing, convert from local format to int format */
      .transform((val) => {
        // If already in international format or empty
        if (val === null || val.startsWith('+')) {
          return val;
        }

        // If empty string, store null
        if (val === '') return null;

        // Convert to international format
        return `+61${val.substring(1)}`;
      })
      .pipe(z.string().nullable()),
  })
  /** Validate: `isClosed` must only be when status is CANCELLED or COMPLETED */
  .refine(
    (order) => {
      return order.isClosed === isOrderClosed(order.status);
    },
    {
      message: 'order.status is not in sync with order.isClosed.',
      path: ['isClosed'],
    },
  );

// 3. Convex Storage Schema - Database shape
// Server-generated fields become required
export const orderCvxSchema = orderSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
  version: z.number().min(1).optional(), // Remove .optional() after a Convex migration
});
export type OrderCvx = z.output<typeof orderCvxSchema>;

// Discriminated unions - See https://github.com/colinhacks/zod#discriminated-unions
// Useful when working with forms (eg delivery form).

const onlineOrderFields = {
  customerName: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  customerPhoneNumber: z.string().min(10, { message: 'Mobile must be 10 digits' }),
};
export const orderPlatformSchema = z.discriminatedUnion('platform', [
  // 'WEB' specific validation rules (guest checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_WEB'),
  }),
  // 'IOS' specific validation rules (customer checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_IOS'),
    restaurantCustomerId: z.string(),
  }),
  // 'ANDROID' specific validation rules (customer checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_ANDROID'),
    restaurantCustomerId: z.string(),
  }),

  // 'POS' specific validation rules.
  orderStrictSchema.safeExtend({ platform: z.literal('POS') }),
]);
export type OrderPlatform = z.output<typeof orderPlatformSchema>;

export const orderServiceTypeSchema = z.discriminatedUnion('serviceType', [
  // 'DELIVERY' specific validation rules.
  orderStrictSchema.safeExtend({
    deliveryAddress: z.string().min(1),
    deliveryNotes: z.string().nullable().default(null),
    deliverySuburbFee: zPrice.min(0),
    deliverySuburbName: z.string().min(1),
    deliverySuburbPostcode: z.string().min(4).max(4),
    deliverySuburbUuid: z.string().min(1),
    serviceType: z.literal('DELIVERY'),
  }),
  // 'PICKUP' specific validation rules.
  orderStrictSchema.safeExtend({ serviceType: z.literal('PICKUP') }),
  // 'TABLE' specific validation rules.
  orderStrictSchema.safeExtend({ serviceType: z.literal('TABLE') }),
]);
export type OrderServiceType = z.output<typeof orderServiceTypeSchema>;

/** Envelope type wrapping an order with restaurant context. */
export const orderWrapSchema = z.object({
  order: orderCvxSchema,
  restaurantName: z.string(),
  restaurantTimeZone: z.string(),
  restaurantStripeConnectAccountId: z.string().nullish(),
});
export type OrderWrap = z.output<typeof orderWrapSchema>;
